import { useMemo } from 'react';

import {
  type Court,
  type DrawDefinition,
  EventTypeEnum,
  MatchUpStatusEnum,
  type MatchUpTypeEnum,
  type Participant,
  type TieFormat,
  type Venue,
} from '@clubspark-node/clubspark-tennis-types';
import { type IOrderOfFinish } from '@clubspark-react/ui';
import dayjs from 'dayjs';
import { useAtomValue } from 'jotai';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import {
  drawDefinitionConstants,
  entryStatusConstants,
  tools as factoryTools,
  tournamentEngine,
} from 'tods-competition-factory';

import { localTimeZoneAtom, tournamentIdAtom } from 'atoms/tournament';
import { getPlayoffGroups } from 'components/dialogs/addPlayoffDialog/utils';
import { calcEliminationDrawSize, getSeedsCount } from 'components/dialogs/draw/utils';
import { type AddDeleteParams } from 'components/dialogs/scorecardTemplate/ScorecardTemplateOverlay';
import { type HydratedCollectionDefinition } from 'components/dialogs/scorecardTemplate/utils';
import { type AdvancementConfigDrawValues } from 'components/drawAdvancementConfig/utils';
import { SEEDING } from 'components/panels/eventsSplitDraws/selectedEvent/rankingsAndRatings/constants';
import { getScheduleChange } from 'components/scheduleDisplay/utils';
import { type SignedInStatusEnum } from 'components/tables/players/PlayersTable';
import { COMPETITION_ENGINE, TOURNAMENT_ENGINE } from 'config/graphql/constants';
import { getQualifyingDrawsStatus, ROUND_ROBIN_GROUP_SIZE_LIMIT, supportedDrawTypes } from 'constants/draws';
import { getRelevantDrawIds } from 'containers/eventsSplitDraws/entries/utils';
import { type VenueFormData } from 'containers/facilities/utils';
import { type PublishSeedingProps } from 'typedefs/seedingTypes';
import { type MatchUpInterface } from 'typedefs/store/scheduleTypes';
import {
  type Event,
  type EventInterface,
  type FlightProfile,
  type ParticipantInterface,
} from 'typedefs/store/tmxTypes';
import { type ScheduleMultiMatchArgsInterface } from 'typedefs/todsCompetitionFactoryTypes';
import { getSeedingScaleName } from 'utils/seeds';

import { useSaveTournament } from './useSaveTournament';

export type IMethod =
  | 'addAdHocMatchUps'
  | 'addAdHocMatchUpsToRound'
  | 'addDrawDefinition'
  | 'addPlayerToTeam'
  | 'addPlayoff'
  | 'addTeam'
  | 'addToDraw'
  | 'addToEvent'
  | 'addVenue'
  | 'assignParticipant'
  | 'bulkRescheduleMatchUps'
  | 'cancelMatchUp'
  | 'changeCourtStatus'
  | 'changeVenueStatus'
  | 'checkIn'
  | 'clearSeeds'
  | 'createAndAttachVoluntaryConsolation'
  | 'deleteAdHocMatchUps'
  | 'deleteSplitDraws'
  | 'deleteTeam'
  | 'destroyPair'
  | 'editDraw'
  | 'editSplitDraw'
  | 'editTeamMatchPlayer'
  | 'editTeamMatchScore'
  | 'enableTieAutoCalc'
  | 'generateEliminationStructurePlayoffs'
  | 'insertNewDraw'
  | 'modifyCourt'
  | 'modifyVenue'
  | 'moveTo'
  | 'moveToMainDraw'
  | 'publishAllEvents'
  | 'publishDraw'
  | 'publishSeeding'
  | 'removeCollectionDefinition'
  | 'removeFromDraw'
  | 'removeParticipant'
  | 'removePlayerFromTeam'
  | 'resetScorecardLineup'
  | 'resetScorecardScores'
  | 'resetVoluntaryConsolationStructure'
  | 'saveCollectionDefinitions'
  | 'saveOrderOfPlay'
  | 'saveSeeds'
  | 'scheduleMultiMatch'
  | 'setEntryPositions'
  | 'setMatchUpHomeParticipant'
  | 'setPlayerStatus'
  | 'signIn'
  | 'syncRoster'
  | 'toggleQualifying'
  | 'unpublishSeeding'
  | 'winByDefault'
  | 'withdrawParticipantFromEvent'
  | 'setOrderOfFinish';

const { DIRECT_ACCEPTANCE, ALTERNATE, UNGROUPED, WITHDRAWN, STRUCTURE_SELECTED_STATUSES } = entryStatusConstants;
const { MAIN, VOLUNTARY_CONSOLATION, QUALIFYING, TOP_DOWN, BOTTOM_UP, ROUND_ROBIN_WITH_PLAYOFF, ROUND_ROBIN } =
  drawDefinitionConstants;
const { DOUBLES } = EventTypeEnum;

const useMutateTournament = (method: IMethod): [(...args: any[]) => Promise<any>, boolean] => {
  const { t } = useTranslation();
  const { saveTournament, saving: isSaving } = useSaveTournament();
  const localTimeZone = useAtomValue(localTimeZoneAtom);
  const tournamentId = useAtomValue(tournamentIdAtom);

  const methods: Record<IMethod, (...args: any[]) => Promise<any>> = useMemo(() => {
    const addToEvent = async (
      eventId: string,
      participantIds: string[],
      isSingle: any,
      drawId: any,
      isTeam = false,
    ) => {
      const methods = [
        {
          method: 'addEventEntries',
          params: {
            drawId,
            eventId,
            participantIds,
            ignoreEventGender: true,
            entryStatus: isSingle || isTeam ? ALTERNATE : UNGROUPED,
          },
        },
      ];
      const res = await saveTournament('addToEvent', TOURNAMENT_ENGINE, methods);
      if (res?.data?.mutateTournament?.success) {
        toast.success(
          t(isTeam ? 'addToEventDialog.numberTeamsAddedToEvent' : 'addToEventDialog.numberPlayersAddedToEvent', {
            number: participantIds?.length,
          }),
        );
      }
      return res;
    };
    return {
      addAdHocMatchUps: async (
        drawDefinition: DrawDefinition,
        numOfRounds: number,
        numOfMatchupsPerRound: number,
        autoGenerate: boolean,
        eventId: string,
      ) => {
        const matchUps = [];
        if (autoGenerate) {
          const participantIds = drawDefinition.entries?.map((entry) => entry.participantId);

          const drawMaticResult = await tournamentEngine.drawMatic({
            addToStructure: true,
            restrictRoundsCount: false,
            drawId: drawDefinition?.drawId,
            participantIds,
            roundsCount: numOfRounds,
            scaleAccessor: 'utrRating',
            scaleName: 'UTR',
            generateMatchUps: true,
            enableDoubleRobin: true,
          });

          if (drawMaticResult.error) {
            toast.error(drawMaticResult.info);
            return;
          }

          matchUps.push(...(drawMaticResult.matchUps ?? []));
        } else {
          const roundsResult = await tournamentEngine.generateAdHocRounds({
            drawDefinition,
            restrictRoundsCount: false,
            restrictMatchUpsCount: false,
            structureId: drawDefinition?.structures?.[0]?.structureId,
            event: { eventId },
            roundsCount: numOfRounds,
            matchUpsCount: numOfMatchupsPerRound || undefined,
          });

          if (roundsResult.error) {
            toast.error(roundsResult.info);
            return;
          }

          matchUps.push(...(roundsResult.matchUps ?? []));
        }

        const methods = [
          {
            method: 'addAdHocMatchUps',
            params: {
              drawId: drawDefinition.drawId,
              matchUps,
            },
          },
        ];

        await saveTournament('addAdHocMatchUps', TOURNAMENT_ENGINE, methods);
      },
      addAdHocMatchUpsToRound: async (drawDefinition: DrawDefinition, roundNumber: number, numOfMatchUps: number) => {
        const { matchUps } = tournamentEngine.generateAdHocMatchUps({
          restrictMatchUpsCount: false,
          drawDefinition,
          structureId: drawDefinition?.structures?.[0]?.structureId,
          roundNumber,
          matchUpsCount: numOfMatchUps,
        });

        const methods = [
          {
            method: 'addAdHocMatchUps',
            params: {
              drawId: drawDefinition?.drawId,
              structureId: drawDefinition?.structures?.[0]?.structureId,
              matchUps,
            },
          },
        ];

        await saveTournament('addAdHocMatchUps', TOURNAMENT_ENGINE, methods);
      },
      deleteAdHocMatchUps: async (matchUpIds: Array<string>, drawId: string, structureId: string) => {
        const methods = [
          {
            method: 'deleteAdHocMatchUps',
            params: {
              tournamentId,
              drawId,
              removeUnAssigned: true,
              removeIncomplete: true,
              removeCompleted: true,
              matchUpIds,
              structureId,
            },
          },
        ];

        await saveTournament('deleteAdHocMatchUps', TOURNAMENT_ENGINE, methods);
      },
      addPlayerToTeam: async (playerIds: string[], teamId: string) => {
        const methods = [
          {
            method: 'addIndividualParticipantIds',
            params: {
              groupingParticipantId: teamId,
              individualParticipantIds: playerIds,
            },
          },
        ];
        const res = await saveTournament('addToTeam', TOURNAMENT_ENGINE, methods);
        if (res?.data?.mutateTournament?.success) {
          toast.success(`${playerIds.length} players sucessfully added to team`);
        }
        return res;
      },
      addPlayoff: async (playoffData: AdvancementConfigDrawValues, drawId: string, structureId: string) => {
        const playoffGroups = getPlayoffGroups({
          drawType: playoffData.playoffStructure,
          advancementType: playoffData.advancementType,
          advancedPerGroup: +playoffData.advancedPerGroup,
          placeRemainingPlayers: playoffData.placeRemainingPlayers,
          groupSize: +playoffData.groupSize,
          t,
        });

        const { structures, links, matchUpModifications } = tournamentEngine.generateAndPopulatePlayoffStructures({
          drawId,
          structureId: structureId,
          playoffGroups,
        });

        if (structures?.some(({ positionAssignments }) => positionAssignments && !positionAssignments?.length)) {
          toast.error(t('invalidConfiguration'));
          return;
        }

        const method = [
          {
            method: 'attachPlayoffStructures',
            params: {
              matchUpModifications,
              structures,
              drawId,
              links,
            },
          },
        ];

        const result = await saveTournament('generatePlayoffs', TOURNAMENT_ENGINE, method);
        if (result.data.mutateTournament.success) toast.success(t('playoffsGenerated'));
      },
      removePlayerFromTeam: async (playerId: string, playerName: string, teamId: string, teamName: string) => {
        const methods = [
          {
            method: 'removeIndividualParticipantIds',
            params: {
              groupingParticipantId: teamId,
              individualParticipantIds: [playerId],
              addIndividualParticipantsToEvents: true,
            },
          },
        ];
        const res = await saveTournament('removeIndividualParticipantIds', TOURNAMENT_ENGINE, methods);
        if (res?.data?.mutateTournament?.success) {
          toast.success(`${playerName} removed from ${teamName}`);
        }
        return res;
      },
      cancelMatchUp: async (matchUp?: MatchUpInterface & { matchUpTieId?: string }) => {
        const methods = [
          {
            method: 'setMatchUpStatus',
            params: {
              matchUpTieId: matchUp?.matchUpTieId,
              matchUpId: matchUp?.matchUpId,
              outcome: { matchUpStatus: MatchUpStatusEnum.CANCELLED },
              drawId: matchUp?.drawId,
            },
          },
        ];
        const result = await saveTournament('setMatchUpStatus', TOURNAMENT_ENGINE, methods);
        if (result?.data?.mutateTournament?.success) {
          toast.success(t('cancelMatchSuccess'));
        }
      },
      saveSeeds: async (updatedScaleItems: any, eventId: string) => {
        const scaleItemsWithParticipantIds = updatedScaleItems.map((item) => ({
          participantId: item.participantId,
          scaleItems: [item.scaleItem],
        }));
        const res = await saveTournament('saveSeeding', TOURNAMENT_ENGINE, [
          {
            method: 'setParticipantScaleItems',
            params: {
              removePriorValues: true,
              scaleItemsWithParticipantIds,
              context: {
                scaleAttributes: { scaleType: SEEDING },
                scaleBasis: { scaleType: 'MANUAL' }, // the attributes used to generate seeding
                eventId,
              },
            },
          },
        ]);
        if (res?.data?.mutateTournament?.success) {
          toast.success('Seeds saved');
        }
        return res;
      },
      publishAllEvents: async (
        events: EventInterface[],
        eventPolicies: {
          participant: any;
          roundNaming: any;
        },
      ) => {
        const methods = [];
        events.forEach((tournamentEvent) => {
          const drawIdsToAdd = tournamentEvent?.drawDefinitions?.map((drawDefinition) => drawDefinition.drawId);
          if (drawIdsToAdd && drawIdsToAdd.length > 0) {
            methods.push({
              params: { drawIdsToAdd, eventId: tournamentEvent.eventId, policyDefinitions: eventPolicies },
              method: 'publishEvent',
            });
          }
        });
        await saveTournament('publishEvent', TOURNAMENT_ENGINE, methods);
      },
      publishDraw: async (
        drawIsPublished: boolean,
        eventPolicies: {
          participant: any;
          roundNaming: any;
        },
        eventId: string,
        drawId: string,
      ) => {
        if (!drawIsPublished) {
          const methods = [
            {
              method: 'publishEvent',
              params: {
                policyDefinitions: eventPolicies,
                drawIdsToAdd: [drawId],
                eventId,
              },
            },
          ];
          await saveTournament('publishEvent', TOURNAMENT_ENGINE, methods);
        } else {
          const methods = [
            {
              method: 'publishEvent',
              params: {
                policyDefinitions: eventPolicies,
                drawIdsToRemove: [drawId],
                eventId,
              },
            },
          ];
          await saveTournament('publishEvent', TOURNAMENT_ENGINE, methods);
        }
      },
      publishSeeding: async (eventId: string, stageSeedingScaleNames: PublishSeedingProps) => {
        const method = {
          method: 'publishEventSeeding',
          params: {
            eventId,
            stageSeedingScaleNames: stageSeedingScaleNames,
          },
        };
        const res = await saveTournament('publishEventSeeding', TOURNAMENT_ENGINE, [method]);
        if (res?.data?.mutateTournament?.success) {
          toast.success('Seeding published');
        }
        return res;
      },
      unpublishSeeding: async (eventId: string, stage?: 'MAIN' | 'QUALIFYING') => {
        const method = {
          method: 'unPublishEventSeeding',
          params: { eventId, ...(stage && { stages: [stage] }) },
        };

        const res = await saveTournament('unPublishEventSeeding', TOURNAMENT_ENGINE, [method]);
        if (res?.data?.mutateTournament?.success) {
          toast.success('Seeding unpublished');
        }
      },
      clearSeeds: async (eventId: string, disableToastMessage?: boolean, scaleName?: string) => {
        const method = {
          method: 'removeSeeding',
          params: { eventId, scaleName },
        };
        const res = await saveTournament('removeSeeding', TOURNAMENT_ENGINE, [method]);
        if (res?.data?.mutateTournament?.success && !disableToastMessage) {
          toast.success('Seeds cleared');
        }
        return res;
      },
      withdrawParticipantFromEvent: async (
        participantIds: string | string[],
        moveTo: 'ALTERNATE' | 'WITHDRAWN' | 'UNGROUPED',
        eventId: string,
      ) => {
        const methods = [];
        participantIds = Array.isArray(participantIds) ? participantIds : [participantIds];
        const relevantDrawIds = getRelevantDrawIds({ participantIds, eventId, tournamentEngine });

        if (relevantDrawIds?.length) {
          relevantDrawIds.forEach((drawId) => {
            methods.push({
              method: 'removeDrawEntries',
              params: {
                drawId,
                eventId,
                participantIds,
              },
            });
            methods.push({
              method: 'modifyEntriesStatus',
              params: {
                eventId,
                participantIds,
                entryStatus: moveTo || WITHDRAWN,
                entryStage: MAIN,
                eventSync: true,
                extension: { name: 'statusDetail', value: 'undefined' },
              },
            });
          });
        } else {
          methods.push({
            method: 'modifyEntriesStatus',
            params: {
              eventId,
              participantIds,
              entryStatus: moveTo || WITHDRAWN,
              entryStage: MAIN,
              eventSync: true,
              extension: { name: 'statusDetail', value: 'undefined' },
            },
          });
        }
        const res = await saveTournament('withdraw', TOURNAMENT_ENGINE, methods);
        if (res?.data?.mutateTournament?.success) {
          toast.success(`Participants sucessfully moved`);
        }
        return res;
      },
      moveToMainDraw: async (participantIds: string[], eventId: string) => {
        const method = {
          method: 'promoteAlternates',
          params: {
            eventId,
            participantIds: participantIds,
          },
        };
        const res = await saveTournament('moveToMainDraw', TOURNAMENT_ENGINE, [method]);
        if (res?.data?.mutateTournament?.success) {
          toast.success(`Participants sucessfully promoted`);
        }
        return res;
      },
      moveTo: async (
        participantId: string | string[],
        event: Event,
        isTeamIndividualParticipant?: boolean,
        entryStage?: 'MAIN' | 'QUALIFYING',
      ) => {
        const methods = [];
        const isDouble = event?.eventType === DOUBLES;
        const eventId = event?.eventId;
        const relevantDrawIds = getRelevantDrawIds({
          participantIds: [participantId],
          eventId,
          tournamentEngine,
        });

        const status =
          (entryStage && !isTeamIndividualParticipant && DIRECT_ACCEPTANCE) ||
          ((isDouble || isTeamIndividualParticipant) && UNGROUPED) ||
          ALTERNATE;

        const participantIds = Array.isArray(participantId) ? participantId : [participantId];
        const addStatusDetail = isTeamIndividualParticipant && entryStage === MAIN;

        if (relevantDrawIds?.length) {
          relevantDrawIds.forEach((drawId) => {
            methods.push({
              method: 'modifyEntriesStatus',
              params: {
                drawId,
                eventId,
                participantIds,
                entryStatus: status,
                entryStage,
                eventSync: true,
                ...(addStatusDetail && { extension: { name: 'statusDetail', value: 'available' } }),
              },
            });
          });
        } else {
          methods.push({
            method: 'modifyEntriesStatus',
            params: {
              eventId,
              participantIds,
              eventSync: true,
              entryStatus: status,
              entryStage,
              ...(addStatusDetail && { extension: { name: 'statusDetail', value: 'available' } }),
            },
          });
        }
        const res = await saveTournament('moveTo', TOURNAMENT_ENGINE, methods);
        if (res?.data?.mutateTournament?.success) {
          toast.success(`Participants sucessfully moved`);
        }
        return res;
      },
      addToDraw: async (selectedDrawsIds: string[], participantIds: string[], eventId: string) => {
        const { flightProfile } = tournamentEngine.getFlightProfile({ eventId });

        const method = selectedDrawsIds.map((drawId) => {
          const isVoluntaryConsolation =
            flightProfile?.flights?.some((flight) => {
              return (
                (!drawId || flight.drawId === drawId) &&
                flight.drawDefinition?.structures?.some((structure) => structure.stage === VOLUNTARY_CONSOLATION)
              );
            }) ?? false;
          return {
            method: 'addDrawEntries',
            params: {
              eventId,
              drawId,
              participantIds,
              entryStatus: DIRECT_ACCEPTANCE,
              ignoreStageSpace: true,
              ...(isVoluntaryConsolation ? { entryStage: VOLUNTARY_CONSOLATION } : { stage: MAIN }),
            },
          };
        });
        const res = await saveTournament('addToDraw', TOURNAMENT_ENGINE, method);
        if (res?.data?.mutateTournament?.success) {
          toast.success(`Participants sucessfully added to draw`);
        }
        return res;
      },
      createAndAttachVoluntaryConsolation: async (drawId: string, tieFormat: TieFormat) => {
        const result = tournamentEngine.generateVoluntaryConsolation({
          attachConsolation: false,
          automated: true,
          drawId: drawId,
          structureName: 'CONSOLATION',
          tieFormat,
        });

        if (result.error) {
          toast.error(result.error.message);
          return;
        }

        const { structures, links } = result;
        const structuresWithTieFormat = structures?.map((structure) => {
          return { ...structure, tieFormat };
        });

        const methods = [
          {
            method: 'attachStructures',
            params: {
              structures: structuresWithTieFormat,
              links,
              drawId,
            },
          },
        ];
        return await saveTournament('attachConsolationStructures', TOURNAMENT_ENGINE, methods);
      },
      removeFromDraw: async (
        selectedDraws: string[],
        participantIds: string[],
        eventId: string,
        isVoluntaryConsolation?: boolean,
      ) => {
        const method = selectedDraws.map((drawId) => ({
          method: 'removeDrawEntries',
          params: {
            drawId,
            eventId,
            participantIds,
            ...(!!isVoluntaryConsolation && { stages: [VOLUNTARY_CONSOLATION] }),
          },
        }));
        const res = await saveTournament('removeFromDraw', TOURNAMENT_ENGINE, method);
        if (res?.data?.mutateTournament?.success) {
          toast.success(`Participants sucessfully removed from draw`);
        }
        return res;
      },
      toggleQualifying: async (eventId: string, enableQualifying?: boolean) => {
        const methods = [
          {
            method: 'addEventExtension',
            params: {
              eventId,
              extension: {
                name: 'eventProfile',
                value: { qualifying: enableQualifying },
              },
            },
          },
        ];
        const res = await saveTournament('toggleQualifying', TOURNAMENT_ENGINE, methods);
        if (res?.data?.mutateTournament?.success) {
          toast.success(`Qualifying sucessfully ${enableQualifying ? 'enabled' : 'removed'}`);
        }
        return res;
      },
      addToEvent,
      editTeamMatchPlayer: async ({
        action,
      }: {
        action: {
          existingParticipantIds: string[];
          newParticipantIds?: string[];
          enforceGender: boolean;
          selectedParticipantId?: string[];
          method: string;
          payload: {
            drawId: string;
            matchUpId?: string;
            tieMatchUpId: string;
            sideNumber?: number;
          };
        };
      }) => {
        const { method, existingParticipantIds, newParticipantIds, enforceGender, selectedParticipantId, payload } =
          action;
        const { drawId, tieMatchUpId, matchUpId, sideNumber } = payload;
        const methods = [];

        switch (method) {
          case 'replaceTieMatchUpParticipantId':
            [...newParticipantIds]?.forEach((newParticipantId, index) =>
              methods.push({
                method,
                params: {
                  drawId,
                  newParticipantId,
                  existingParticipantId: existingParticipantIds[index],
                  tieMatchUpId,
                  enforceGender,
                },
              }),
            );
            break;
          case 'substituteParticipant':
            [...newParticipantIds]?.forEach((newParticipantId, index) =>
              methods.push({
                method,
                params: {
                  drawId,
                  substituteParticipantId: newParticipantId,
                  existingParticipantId: selectedParticipantId?.[index],
                  matchUpId,
                },
              }),
            );
            break;
          case 'assignTieMatchUpParticipantId': {
            [...existingParticipantIds]?.forEach((existingParticipantId) =>
              methods.push({
                method: 'removeTieMatchUpParticipantId',
                params: { drawId, participantId: existingParticipantId, tieMatchUpId },
              }),
            );
            [...newParticipantIds]?.forEach((newParticipantId) =>
              methods.push({
                method,
                params: { drawId, participantId: newParticipantId, tieMatchUpId, sideNumber, enforceGender },
              }),
            );
            break;
          }
          case 'removeTieMatchUpParticipantId':
            [...existingParticipantIds]?.forEach((existingParticipantId) =>
              methods.push({
                method: 'removeTieMatchUpParticipantId',
                params: { drawId, participantId: existingParticipantId, tieMatchUpId },
              }),
            );
            break;
        }

        return await saveTournament('editParticipant', TOURNAMENT_ENGINE, methods);
      },
      addTeam: async (team: ParticipantInterface, eventId: string, isEdit: boolean) => {
        const methods = [
          {
            method: isEdit ? 'modifyParticipant' : 'addParticipant',
            params: { participant: team },
          },
        ];
        const res = await saveTournament('addTeam', TOURNAMENT_ENGINE, methods);
        if (res?.data?.mutateTournament?.success && !isEdit && eventId !== 'noEvent') {
          addToEvent(eventId, [team.participantId], false, undefined, true);
        }
        return res;
      },
      deleteTeam: async (teamId: string, eventId: string | null, teamName: string) => {
        const methods = eventId
          ? [
              {
                method: 'destroyPairEntry',
                params: {
                  participantId: teamId,
                  eventId: eventId,
                  entryStatus: UNGROUPED,
                  removeGroupParticipant: true,
                },
              },
            ]
          : [
              {
                method: 'deleteParticipants',
                params: { participantIds: [teamId], addIndividualParticipantsToEvents: true },
              },
            ];

        const res = await saveTournament('deleteTeam', TOURNAMENT_ENGINE, methods);
        if (res?.data?.mutateTournament?.success) {
          toast.success(`${teamName} ${t('teams.deleteSuccess')}`);
        }
        return res;
      },
      signIn: (participantId: string, signInState: SignedInStatusEnum) => {
        const methods = [
          {
            method: 'modifyParticipantsSignInStatus',
            params: { participantIds: [participantId], signInState },
          },
        ];
        return saveTournament('signIn', TOURNAMENT_ENGINE, methods);
      },
      addDrawDefinition: async (
        event: Event,
        drawDefinitionValues,
        flight?: FlightProfile,
        tournamentPolicies?: any,
      ) => {
        const { isAddMainDrawToQualifying, isAddQualifyingDrawToMain } = getQualifyingDrawsStatus(
          event?.drawDefinitions?.[0],
        );

        const isQualifying = drawDefinitionValues?.drawStage === QUALIFYING;

        if (isAddMainDrawToQualifying && event?.drawDefinitions?.[0]?.drawId) {
          drawDefinitionValues.drawId = event?.drawDefinitions?.[0]?.drawId;
        }

        drawDefinitionValues.policyDefinitions = tournamentPolicies;
        const isRoundRobin = [supportedDrawTypes.ROUND_ROBIN, supportedDrawTypes.ROUND_ROBIN_WITH_PLAYOFF]
          .filter((f) => f)
          .includes(drawDefinitionValues.drawType);
        if (isRoundRobin) drawDefinitionValues.enforcePolicyLimits = false;

        const enteredParticipantsCount =
          event?.entries?.filter((entry) => STRUCTURE_SELECTED_STATUSES.includes(entry.entryStatus))?.length || 0;
        const drawSize = parseInt(flight?.drawSize || drawDefinitionValues.drawSize);
        drawDefinitionValues.drawSize = drawSize;

        const drawEntries = (flight?.drawEntries || event?.entries || []).filter(({ entryStatus }) =>
          STRUCTURE_SELECTED_STATUSES.includes(entryStatus),
        );

        Object.assign(drawDefinitionValues, flight, {
          drawName: drawDefinitionValues.drawName,
          drawSize,
          drawEntries,
        });

        const playoffGroups = getPlayoffGroups({
          drawType: drawDefinitionValues.playoffStructure,
          advancementType: drawDefinitionValues.advancementType,
          advancedPerGroup: +drawDefinitionValues.advancedPerGroup,
          placeRemainingPlayers: drawDefinitionValues.placeRemainingPlayers,
          groupSize: drawDefinitionValues.structureOptions?.groupSize,
          t,
        });

        if ([ROUND_ROBIN, ROUND_ROBIN_WITH_PLAYOFF].includes(drawDefinitionValues.drawType)) {
          drawDefinitionValues.structureOptions = {
            groupSize: drawDefinitionValues.structureOptions.groupSize,
            groupSizeLimit: ROUND_ROBIN_GROUP_SIZE_LIMIT,
            playoffGroups,
          };
        }

        const { drawType, structureOptions } = drawDefinitionValues;
        const groupSize = structureOptions?.groupSize || 1;
        const drawPositionsCount = Math.ceil(drawSize / groupSize) * groupSize;
        const invalid_RRPO = drawType === ROUND_ROBIN_WITH_PLAYOFF && drawPositionsCount / groupSize < 2;
        const invalid_RR = [ROUND_ROBIN_WITH_PLAYOFF, ROUND_ROBIN].includes(drawType) && drawPositionsCount < 3;
        if (invalid_RRPO || invalid_RR) {
          toast.error('Invalid Configuration');
          return;
        }

        drawDefinitionValues.enforceMinimumDrawSize = false;
        drawDefinitionValues.hydrateCollections = true;
        drawDefinitionValues.seedByRanking = false;
        drawDefinitionValues.seedingScaleName = getSeedingScaleName(event?.eventId, flight?.drawId, isQualifying);
        drawDefinitionValues.ignoreAllowedDrawTypes = true;
        drawDefinitionValues.feedPolicy = {
          roundGroupedOrder: [
            [1], // complete round TOP_DOWN
            [1], // complete round BOTTOM_UP
            [1, 2], // 1st half BOTTOM_UP, 2nd half BOTTOM_UP
            [3, 4, 1, 2], // 3rd Qtr BOTTOM_UP, 4th Qtr BOTTOM_UP, 1st BOTTOM_UP, 2nd Qtr BOTTOM_UP
            [2, 1, 4, 3, 6, 5, 8, 7],
            [1], // complete round BOTTOM_UP
          ],
          roundFeedProfiles: [TOP_DOWN, BOTTOM_UP, BOTTOM_UP, BOTTOM_UP, BOTTOM_UP, BOTTOM_UP, BOTTOM_UP, BOTTOM_UP],
        };

        if (drawDefinitionValues.automated === 'manual') drawDefinitionValues.automated = false;
        drawDefinitionValues.finishingPositionNaming = { '3-4': { name: '3-4 Playoff', abbreviation: 'PL' } };

        const maxSeeds = getSeedsCount(calcEliminationDrawSize(enteredParticipantsCount), drawEntries, {
          seeding: tournamentPolicies?.seeding,
        });

        if (drawDefinitionValues.seedsCount > maxSeeds) {
          drawDefinitionValues.enforcePolicyLimits = false;
        }

        if (drawDefinitionValues.qualifiersCount > 0) drawDefinitionValues.qualifyingPlaceholder = true;

        const qualifyingProfiles = [
          {
            structureProfiles: [
              {
                drawSize: drawDefinitionValues?.drawSize,
                matchUpFormat: drawDefinitionValues.matchUpFormat,
                qualifyingPositions: drawDefinitionValues?.qualifiersCount,
                seedingScaleName: getSeedingScaleName(event?.eventId, flight?.drawId, isQualifying),
                seedsCount: drawDefinitionValues?.seedsCount,
              },
            ],
          },
        ];

        const qualifyingDrawDefinitionValues = {
          eventId: event?.eventId,
          automated: drawDefinitionValues.automated,
          qualifyingOnly: !isAddQualifyingDrawToMain,
          drawName: drawDefinitionValues.drawName,
          matchUpFormat: drawDefinitionValues.matchUpFormat,
          qualifyingProfiles,
          ignoreStageSpace: true,
          tieFormat: drawDefinitionValues.tieFormat,
        };

        const addQualifyingDrawToMainParams = {
          drawName: drawDefinitionValues.drawName,
          automated: drawDefinitionValues.automated,
          drawId: event?.drawDefinitions?.[0]?.drawId,
          drawEntries,
          qualifyingProfiles,
          ignoreStageSpace: true,
        };

        const getDrawDefinitionParams = () => {
          if (isAddQualifyingDrawToMain) return addQualifyingDrawToMainParams;
          if (isQualifying) return qualifyingDrawDefinitionValues;
          return drawDefinitionValues;
        };

        const result = tournamentEngine.generateDrawDefinition(getDrawDefinitionParams());

        if (result.error) {
          toast.error(result.error.message);
          return;
        }
        const { drawDefinition } = result || {};
        // structure generation fails with invalid values
        // do not save drawDefinition with no structures
        const existingDrawCount = event?.drawDefinitions?.length || 0;
        if (drawDefinition?.structures?.length) {
          const method = [
            {
              method: 'addDrawDefinition',
              params: {
                eventId: event?.eventId,
                drawDefinition,
                flight,
                existingDrawCount,
                allowReplacement: true,
                ...(flight?.drawId &&
                  (isAddMainDrawToQualifying || isAddQualifyingDrawToMain) && { drawId: flight?.drawId }),
              },
            },
          ];

          await saveTournament('addDraw', TOURNAMENT_ENGINE, method);
        }
      },
      insertNewDraw: (eventId: string, drawName: string) => {
        const methods = [
          {
            method: 'addFlight',
            params: { eventId, drawName, drawId: factoryTools.UUID() },
          },
        ];
        return saveTournament('insertDraw', TOURNAMENT_ENGINE, methods);
      },
      editDraw: (eventId: string, oldDrawDefinition: DrawDefinition | undefined, newDrawDefinition: DrawDefinition) => {
        if (oldDrawDefinition?.drawName === newDrawDefinition.drawName) {
          return;
        }

        const methods = [
          {
            method: 'modifyDrawDefinition',
            params: {
              drawUpdates: { drawName: newDrawDefinition.drawName.trim() },
              drawId: newDrawDefinition.drawId,
              eventId: eventId,
            },
          },
        ];
        return saveTournament('editDraw', TOURNAMENT_ENGINE, methods);
      },
      editSplitDraw: (eventId: string, oldFlights: FlightProfile[], newFlights: FlightProfile[]) => {
        const methods: any[] = [];

        const oldFlightsMap: Record<string, any> = oldFlights.reduce(
          (prev, curr) => ({ ...prev, [curr.drawId]: curr }),
          {},
        );
        const newFlightsMap: Record<string, any> = newFlights.reduce(
          (prev, curr) => ({ ...prev, [curr.drawId]: curr }),
          {},
        );

        const reorderedFlights = [];
        const renamedFlights = [];

        Object.entries(newFlightsMap).forEach(([key, newFlight]) => {
          const oldFlight = oldFlightsMap[key];

          if (oldFlight.flightNumber !== newFlight.flightNumber) {
            reorderedFlights.push(newFlight);
          }

          if (oldFlight.drawName !== newFlight.drawName) {
            renamedFlights.push(newFlight);
          }
        });

        if (reorderedFlights.length) {
          const orderedDrawIdsMap = newFlights.reduce(
            (curr, { drawId }, ind) => ({
              ...curr,
              [drawId]: ind + 1,
            }),
            {},
          );

          methods.push({
            method: 'updateDrawIdsOrder',
            params: {
              eventId: eventId,
              orderedDrawIdsMap,
            },
          });
        }

        renamedFlights.forEach(({ drawId, drawName = '' }) => {
          methods.push({
            method: 'modifyDrawDefinition',
            params: {
              drawUpdates: { drawName: drawName.trim() },
              eventId: eventId,
              drawId,
            },
          });
        });

        if (methods.length) {
          return saveTournament('editSplitDraw', TOURNAMENT_ENGINE, methods);
        }
      },
      deleteSplitDraws: (eventId: string, drawIds: string[]) => {
        const methods = drawIds.map((drawId) => ({
          method: 'deleteFlightAndFlightDraw',
          params: { drawId, eventId: eventId, force: true },
        }));
        return saveTournament('deleteDraws', TOURNAMENT_ENGINE, methods);
      },
      saveCollectionDefinitions: async ({
        collectionDefinitions,
        deletedDefinitions,
        addedDefinitions,
        drawId,
        structureId,
        matchUpId,
        allMatchUpsCount,
      }: {
        collectionDefinitions: HydratedCollectionDefinition[];
        deletedDefinitions: HydratedCollectionDefinition[];
        addedDefinitions: HydratedCollectionDefinition[];
        drawId: string;
        structureId: string;
        matchUpId: string;
        allMatchUpsCount: number;
      }) => {
        let methods = deletedDefinitions.map((deletedDefinition) => {
          const params: AddDeleteParams = {
            collectionId: deletedDefinition.collectionId,
            tieFormatComparison: !matchUpId,
            tieFormatName: deletedDefinition.collectionName,
            structureId: structureId,
            drawId: drawId,
            updateInProgressMatchUps: false,
            matchUpId: matchUpId ?? undefined,
          };
          return {
            method: 'removeCollectionDefinition',
            params,
          };
        });

        addedDefinitions.forEach((addDef) => {
          const uuids = factoryTools.UUIDS(allMatchUpsCount * addDef.matchUpCount);
          const params = {
            collectionDefinition: addDef,
            tieFormatName: addDef.collectionName,
            structureId: structureId,
            drawId: drawId,
            uuids,
            updateInProgressMatchUps: false,
            matchUpId: matchUpId ?? undefined,
          };

          methods.push({
            method: 'addCollectionDefinition',
            params: params,
          });
        });

        const editedDefinitionMethods = collectionDefinitions
          .filter((def) => def.isEdited)
          .map((def) => {
            return {
              method: 'modifyCollectionDefinition',
              params: {
                collectionName: def.collectionName,
                matchUpFormat: def.matchUpFormat,
                collectionId: def.collectionId,
                structureId: structureId,
                drawId: drawId,
                matchUpValue: def.matchUpValue,
                collectionValue: def.collectionValue,
                gender: def.gender,
                category: def.category,
                scoreValue: def.scoreValue,
                setValue: def.setValue,
                matchUpId: matchUpId ?? undefined,
              },
            };
          });

        if (editedDefinitionMethods?.length) {
          methods = methods.concat(editedDefinitionMethods);
        }

        const reorderedDefinitions = {
          method: 'orderCollectionDefinitions',
          params: {
            orderMap: collectionDefinitions
              .map((def, index) => {
                return {
                  collectionId: def.collectionId,
                  order: index + 1,
                };
              })
              .reduce((acc, cur) => ({ ...acc, [cur.collectionId]: cur.order }), {}),
            structureId: structureId,
            drawId: drawId,
            matchUpId: matchUpId ?? undefined,
          },
        };

        methods.push(reorderedDefinitions);
        const result = await saveTournament('saveCollectionDef', TOURNAMENT_ENGINE, methods);
        if (result.data?.mutateTournament?.success) {
          toast.success(t('scorecardUpdatedSuccess'));
        }
        return result;
      },
      removeCollectionDefinition: async (matchUp: MatchUpInterface, collectionId: string) => {
        const { matchUpId, drawId } = matchUp;
        const methods = [
          {
            method: 'removeCollectionDefinition',
            params: {
              collectionId,
              tieFormatName: matchUp.tieFormat?.tieFormatName,
              updateInProgressMatchUps: true,
              matchUpId,
              drawId,
            },
          },
        ];

        const result = await saveTournament('removeCollectionDefinition', TOURNAMENT_ENGINE, methods);
        if (result.data?.mutateTournament?.success) {
          toast.success(t('teams.tiebreakMatchRemoveSuccess'));
        }
      },
      resetScorecardScores: async (matchUpId: string, drawId: string, isTieBreakAdded?: boolean) => {
        const methods = [
          {
            method: 'enableTieAutoCalc',
            params: {
              matchUpId,
              drawId,
            },
          },
          {
            method: 'resetScorecard',
            params: {
              tiebreakReset: isTieBreakAdded,
              matchUpId,
              drawId,
            },
          },
        ];
        return await saveTournament(`resetScorecardScores-${matchUpId}`, TOURNAMENT_ENGINE, methods);
      },
      resetScorecardLineup: async (matchUpId: string, drawId: string) => {
        const methods = [
          {
            method: 'resetMatchUpLineUps',
            params: {
              inheritance: false,
              matchUpId,
              drawId,
            },
          },
        ];

        return await saveTournament(`resetScorecardLineup-${matchUpId}`, TOURNAMENT_ENGINE, methods);
      },
      resetVoluntaryConsolationStructure: (drawDefinition: DrawDefinition) => {
        const methods = [
          {
            method: 'resetVoluntaryConsolationStructure',
            params: {
              drawId: drawDefinition.drawId,
            },
          },
        ];
        return saveTournament('resetVoluntaryConsolationStructure', TOURNAMENT_ENGINE, methods);
      },
      setEntryPositions: async ({
        drawId,
        eventId,
        entryPositions,
        entryStatus,
      }: {
        drawId: string;
        eventId: string;
        entryPositions: { participantId: string; entryPosition: number }[];
        entryStatus: 'ALTERNATE' | 'QUALIFYING';
      }) => {
        const getAction = () => {
          switch (entryStatus) {
            case ALTERNATE:
              return 'saveAlternatesReorder';
            case QUALIFYING:
              return 'saveQualifyingReorder';
            default:
              return 'saveReorder';
          }
        };

        const method = [
          {
            method: 'setEntryPositions',
            params: { drawId, eventId, entryPositions },
          },
        ];
        return await saveTournament(getAction(), TOURNAMENT_ENGINE, method);
      },
      setMatchUpHomeParticipant: async (participantId: string, isHome: boolean, matchUpId: string, drawId: string) => {
        const method = [
          {
            method: 'setMatchUpHomeParticipantId',
            params: {
              homeParticipantId: !isHome ? participantId : '',
              removePriorValues: true,
              disableNotice: false,
              matchUpId,
              drawId,
            },
          },
        ];

        return await saveTournament('setHomeParticipant', TOURNAMENT_ENGINE, method);
      },
      scheduleMultiMatch: async (values: ScheduleMultiMatchArgsInterface) => {
        const bulkScheduleMethod = {
          method: 'bulkScheduleMatchUps',
          params: values,
        };
        await saveTournament('multiMatch', TOURNAMENT_ENGINE, [bulkScheduleMethod]);
      },
      destroyPair: (eventId: string, pairParticipantId: string, checkedParticipantIds: string[]) => {
        const methods = [];
        const relevantDrawIds = getRelevantDrawIds({
          participantIds: [pairParticipantId],
          eventId,
          tournamentEngine,
        });

        if (relevantDrawIds?.length) {
          relevantDrawIds.forEach((drawId) => {
            methods.push({
              method: 'destroyPairEntry',
              params: { drawId, eventId: eventId, participantId: pairParticipantId },
            });
            methods.push({
              method: 'modifyEntriesStatus',
              params: {
                drawId,
                eventId: eventId,
                participantIds: checkedParticipantIds,
                entryStatus: WITHDRAWN,
                eventSync: true,
              },
            });
          });
        } else {
          methods.push({
            method: 'destroyPairEntry',
            params: { eventId: eventId, participantId: pairParticipantId },
          });
          methods.push({
            method: 'modifyEntriesStatus',
            params: {
              eventId: eventId,
              participantIds: checkedParticipantIds,
              entryStatus: WITHDRAWN,
              eventSync: true,
            },
          });
        }
        return saveTournament('destroyPair', TOURNAMENT_ENGINE, methods);
      },
      setPlayerStatus: (checkedParticipantIds: string[], eventId: string, status: string) => {
        const methods = [
          {
            method: 'modifyEntriesStatus',
            params: {
              eventId: eventId,
              participantIds: checkedParticipantIds,
              entryStatus: status,
              eventSync: true,
            },
          },
        ];
        return saveTournament('modifyEntriesStatus', TOURNAMENT_ENGINE, methods);
      },
      addVenue: async (values: VenueFormData) => {
        values.venueId = factoryTools.UUID();
        const addVenue = {
          method: 'addVenue',
          params: { venue: values },
        };

        const methods = [addVenue];
        return await saveTournament('addVenue', COMPETITION_ENGINE, methods);
      },
      modifyVenue: async (modifications: VenueFormData, venueId: string) => {
        const modifyVenue = {
          method: 'modifyVenue',
          params: { venueId, modifications, force: true },
        };

        const methods = [modifyVenue];
        return await saveTournament('modifyVenue', COMPETITION_ENGINE, methods);
      },
      changeVenueStatus: async (venue: Venue, disabled: boolean) => {
        const method = disabled ? 'enableVenues' : 'disableVenues';
        const methods = [
          {
            method,
            params: {
              venueIds: [venue.venueId],
            },
          },
        ];

        const result = await saveTournament(method, COMPETITION_ENGINE, methods);
        if (result?.data?.mutateTournament?.success) {
          toast.success(`${venue.venueName} has been ${disabled ? 'enabled' : 'disabled'}`);
        }

        return result;
      },
      modifyCourt: async (court: Court, courtName: string) => {
        const methods = [
          {
            method: 'modifyCourt',
            params: {
              courtId: court.courtId,
              force: true,
              modifications: { ...court, courtName },
            },
          },
        ];

        return await saveTournament('modifyCourt', COMPETITION_ENGINE, methods);
      },
      changeCourtStatus: async (court: Court, selectedDate: string, disabled: boolean) => {
        const courtDisabledDates = court.extensions?.find((extension) => extension.name === 'disabled')?.value.dates;
        const dates = !disabled && courtDisabledDates ? [...courtDisabledDates, selectedDate] : [selectedDate];

        const method = disabled ? 'enableCourts' : 'disableCourts';
        const methods = [
          {
            method,
            params: {
              courtIds: [court.courtId],
              dates,
            },
          },
        ];

        const result = await saveTournament(method, COMPETITION_ENGINE, methods);
        if (result?.data?.mutateTournament?.success) {
          const selectedDay = dayjs(selectedDate).format('dddd, MMMM Do YYYY');
          toast.success(`${court.courtName} has been ${disabled ? 'enabled' : 'disabled'} for ${selectedDay}`);
        }

        return result;
      },
      editTeamMatchScore: async (
        matchUpId: string,
        drawId: string,
        outcome: {
          matchUpStatus;
          winningSide;
          score;
        },
        autoCalcScore: boolean,
      ) => {
        const methods = [
          {
            method: 'setMatchUpStatus',
            params: {
              drawId,
              matchUpId,
              outcome,
              ...(autoCalcScore
                ? { enableAutoCalc: true }
                : {
                    disableAutoCalc: true,
                  }),
            },
          },
        ];
        const result = await saveTournament('setMatchUpStatus', TOURNAMENT_ENGINE, methods);
        if (result?.data?.mutateTournament?.success) {
          toast.success('Match score updated');
        }
        return result;
      },
      enableTieAutoCalc: async (matchUpId: string, drawId: string) => {
        const methods = [
          {
            method: 'enableTieAutoCalc',
            params: {
              matchUpId,
              drawId,
            },
          },
        ];

        const result = await saveTournament('enableTieAutoCalc', TOURNAMENT_ENGINE, methods);
        if (result?.data?.mutateTournament?.success) {
          toast.success('Automatic scoring for team match is turned on');
        }
        return result;
      },
      generateEliminationStructurePlayoffs: async (
        options: {
          structureId: string;
          exitProfile: string;
          roundNumber: number;
          label: string;
        }[],
        drawId: string,
      ) => {
        // the selected options may include playoff structures branch off of multiple existing structures
        // the playoff structures must be grouped by the existing structures from which they originate
        const structureOptions = options.reduce((groups, option) => {
          const structureId = option.structureId;
          if (!groups[structureId]) groups[structureId] = [];
          groups[structureId].push(option);
          return groups;
        }, {});

        // playoff structures are generated from each existing structure separately
        const playoffMethods = Object.keys(structureOptions).map((structureId: string) => {
          const options = structureOptions[structureId];
          const playoffAttributes = Object.assign(
            {},
            ...options
              .filter((o) => o.exitProfile)
              .map((o) => ({ [o.exitProfile]: { name: o.label, abbreviation: o.label[0] } })),
          );
          // constrain generated playoff structures to depth of 1 (e.g. don't generate 7-8 for 5-8)
          const roundProfiles = options.map((o) => ({ [o.roundNumber]: 1 }));

          const uuids = factoryTools.UUIDS(100);
          const { structures, links, matchUpModifications } = tournamentEngine.generateAndPopulatePlayoffStructures({
            drawId,
            structureId,
            uuids,
            roundProfiles,
            playoffAttributes,
            playoffStructureNameBase: t('playoffText'),
          });
          return {
            method: 'attachPlayoffStructures',
            params: {
              drawId,
              structures,
              links,
              matchUpModifications,
            },
          };
        });

        const result = await saveTournament('generateEliminationPlayoffs', TOURNAMENT_ENGINE, playoffMethods);
        if (result.data?.mutateTournament?.success) {
          toast.success(t('generatePlayoffsSuccess'));
        }
        if (result.error) {
          toast.error(t('generatePlayoffsError'));
        }
      },
      assignParticipant: async (payload: any) => {
        const params = payload.payload;

        if (payload.method === 'qualifierDrawPositionAssignment') {
          params.qualifyingParticipantId = payload.participant.participantId;
        }
        if (['assignDrawPosition', 'assignMatchUpSideParticipant'].includes(payload.method)) {
          params.participantId = payload.participant.participantId;
        }

        if (payload.method === 'luckyLoserDrawPositionAssignment') {
          params.luckyLoserParticipantId = payload.participant.participantId;
        }

        if (payload.method === 'modifyPairAssignment') {
          params.replacementIndividualParticipantId = payload.participant.participantId;
          params.existingIndividualParticipantId = payload.existingIndividualParticipant.participantId;
          params.uuids = [factoryTools.UUID()];
        }
        const methods = [{ method: payload.method, params }];
        const result = await saveTournament('assignParticipant', TOURNAMENT_ENGINE, methods);
        if (result?.data?.mutateTournament?.success) {
          payload.method === 'modifyPairAssignment' && toast.success(t('playerReplaced'));
        }
      },
      removeParticipant: async (drawId: string, sideNumber: number, matchUpId: string, structureId: string) => {
        const methods = [
          {
            method: 'removeMatchUpSideParticipant',
            params: {
              drawId,
              sideNumber,
              matchUpId,
              structureId,
            },
          },
        ];

        await saveTournament('removeMatchUpSideParticipant', TOURNAMENT_ENGINE, methods);
      },
      bulkRescheduleMatchUps: async (
        date: { text: string; value: string },
        hours: number,
        minutes: number,
        direction: 'earlier' | 'later',
        sendSms: boolean,
        selectedDate: { text: string; value: string },
        selectedMatchUps: { matchUpId: string }[],
      ) => {
        const matchUpIds = selectedMatchUps.map((matchUp) => matchUp.matchUpId);
        const scheduleChange = getScheduleChange({
          newDate: date,
          hours,
          minutes,
          direction,
          currentDate: selectedDate,
          localTimeZone,
        });

        const methods = [
          {
            method: 'bulkRescheduleMatchUps',
            params: {
              matchUpIds,
              scheduleChange,
              sendSms,
            },
          },
        ];
        return await saveTournament('bulkRescheduleMatchUps', COMPETITION_ENGINE, methods);
      },
      saveOrderOfPlay: async (type: 'PUBLISH' | 'UNPUBLISH') => {
        const methods = [
          {
            method: type === 'PUBLISH' ? 'publishOrderOfPlay' : 'unPublishOrderOfPlay',
            params: { removePriorValues: true },
          },
        ];
        const savingTournamentResponse = await saveTournament('saveOrderOfPlay', TOURNAMENT_ENGINE, methods);
        if (savingTournamentResponse?.data?.mutateTournament?.success) {
          toast.success(type === 'PUBLISH' ? 'Order of play published' : 'Order of play unpublished');
        }
      },
      checkIn: async (participantId: string, matchUpId: string, matchUps: MatchUpInterface[]) => {
        const matchUpContextIds = tournamentEngine.getMatchUpContextIds({ matchUps, matchUpId });
        const payload = Object.assign(matchUpContextIds, { participantId });
        const methods = [
          {
            method: 'toggleParticipantCheckInState',
            params: payload,
          },
        ];
        await saveTournament('checkIn', COMPETITION_ENGINE, methods);
      },
      winByDefault: async (matchUp?: MatchUpInterface & { matchUpTieId?: string }) => {
        const winningSideNumber = matchUp?.sides?.find((side) => !!side.participant)?.sideNumber;

        const methods = [
          {
            method: 'setMatchUpStatus',
            params: {
              matchUpTieId: matchUp?.matchUpTieId,
              matchUpId: matchUp?.matchUpId,
              outcome: { matchUpStatus: MatchUpStatusEnum.DEFAULTED, winningSide: winningSideNumber },
              drawId: matchUp?.drawId,
            },
          },
        ];
        const result = await saveTournament('setMatchUpStatus', TOURNAMENT_ENGINE, methods);
        if (result?.data?.mutateTournament?.success) {
          toast.success(t('winByDefaultSuccess'));
        }
      },
      syncRoster: async (data: { syncDualMatchRosters: Participant[] }) => {
        if (!data?.syncDualMatchRosters?.length) return;
        const methods = [{ method: 'mergeParticipants', params: { participants: data.syncDualMatchRosters } }];
        await saveTournament('syncRoster', TOURNAMENT_ENGINE, methods);
      },
      setOrderOfFinish: async (drawId: string, data: { [key in MatchUpTypeEnum]: IOrderOfFinish[] }) => {
        const methods = Object.entries(data).reduce((acc, [, values]) => {
          const finishingOrder = values
            .filter(({ orderOfFinish }) => orderOfFinish != null)
            .map(({ matchUpId, orderOfFinish }) => ({ matchUpId, orderOfFinish }));

          if (finishingOrder.length) {
            acc.push({
              method: 'setOrderOfFinish',
              params: {
                finishingOrder,
                drawId,
              },
            });
          }

          return acc;
        }, []);

        return await saveTournament('setOrderOfFinish', TOURNAMENT_ENGINE, methods);
      },
    };
  }, [localTimeZone, saveTournament, t, tournamentId]);

  return useMemo(() => [methods[method], isSaving], [isSaving, method, methods]);
};

export default useMutateTournament;
